import { ToastService } from '@core/services';

const STALE_BUNDLE_RELOAD_ATTEMPTS_KEY = 'auth-app:bundle-reload-attempts';
const MAX_AUTO_RELOAD_ATTEMPTS = 2;
const RELOAD_DELAY_MS = 1500;
const HANDLED_DEDUPE_MS = 2000;

const STALE_BUNDLE_ERROR_PATTERN =
  /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i;

const SSO_FAST_PATH = '/sso-fast';

/** Route con loadComponent (allineate a app.routes.ts). */
const LAZY_ROUTE_PREFIXES = [
  'login',
  'forgot-password',
  'signup',
  'recovery',
  'settings',
  'active-sessions',
  'amministrazione'
];

let lastHandledAt = 0;
let globalListenerInstalled = false;

/** True se l'URL indica una route lazy: reset anti-loop solo dopo caricamento chunk riuscito. */
export function shouldClearStaleBundleReloadAttempts(urlAfterRedirects: string): boolean {
  const path = normalizeRoutePath(urlAfterRedirects);
  return LAZY_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** Azzera i tentativi di reload automatico dopo una navigazione lazy riuscita. */
export function clearStaleBundleReloadAttempts(): void {
  try {
    sessionStorage.removeItem(STALE_BUNDLE_RELOAD_ATTEMPTS_KEY);
  } catch {
    /* ignore */
  }
}

/** True se l'errore indica un bundle/chunk obsoleto dopo un deploy. */
export function isStaleBundleError(error: unknown): boolean {
  return STALE_BUNDLE_ERROR_PATTERN.test(collectErrorText(error));
}

/**
 * Gestisce un errore di bundle obsoleto: reload silenzioso e immediato in flusso SSO,
 * altrimenti toast + reload. Al secondo fallimento consecutivo chiede reload manuale.
 */
export function handleStaleBundleError(toastService: ToastService): void {
  const now = Date.now();
  if (now - lastHandledAt < HANDLED_DEDUPE_MS) {
    return;
  }
  lastHandledAt = now;

  const attempts = getReloadAttempts();
  if (attempts >= MAX_AUTO_RELOAD_ATTEMPTS) {
    if (!isSsoUpdateFlow()) {
      toastService.presentaToastErrore(
        'Impossibile caricare la nuova versione automaticamente. Effettua di nuovo l\'accesso.'
      );
    }
    return;
  }

  incrementReloadAttempts();

  const ssoFlow = isSsoUpdateFlow();
  if (!ssoFlow) {
    toastService.presentaToastInfo(
      'È disponibile una nuova versione. L\'applicazione verrà ricaricata automaticamente…'
    );
  }

  setTimeout(() => location.reload(), ssoFlow ? 0 : RELOAD_DELAY_MS);
}

/** Listener globali per errori chunk fuori dal router (unhandledrejection, error). */
export function installStaleBundleGlobalListener(onStaleBundle: () => void): void {
  if (typeof window === 'undefined' || globalListenerInstalled) {
    return;
  }
  globalListenerInstalled = true;

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isStaleBundleError(event.reason)) {
      return;
    }
    event.preventDefault();
    onStaleBundle();
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    if (!isStaleBundleError(event.error ?? event.message)) {
      return;
    }
    onStaleBundle();
  });
}

/** Flusso SSO: target_app in URL oppure route /sso-fast. */
export function isSsoUpdateFlow(): boolean {
  if (getTargetAppFromHref()) {
    return true;
  }
  return (window.location.pathname || '/') === SSO_FAST_PATH;
}

function getReloadAttempts(): number {
  try {
    const raw = sessionStorage.getItem(STALE_BUNDLE_RELOAD_ATTEMPTS_KEY);
    const parsed = Number.parseInt(raw ?? '0', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function incrementReloadAttempts(): void {
  try {
    sessionStorage.setItem(STALE_BUNDLE_RELOAD_ATTEMPTS_KEY, String(getReloadAttempts() + 1));
  } catch {
    /* ignore */
  }
}

function getTargetAppFromHref(): string | undefined {
  try {
    const url = new URL(window.location.href);
    const fromSearch = url.searchParams.get('target_app');
    if (fromSearch) {
      return fromSearch;
    }
    if (url.hash?.includes('?')) {
      const hashSearch = url.hash.substring(url.hash.indexOf('?') + 1);
      return new URLSearchParams(hashSearch).get('target_app') ?? undefined;
    }
  } catch {
    const match = window.location.href.match(/[?&]target_app=([^&#]+)/);
    if (match) {
      return decodeURIComponent(match[1]);
    }
  }
  return undefined;
}

function collectErrorText(error: unknown): string {
  if (error == null) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    return [obj['message'], obj['name'], obj['error'], obj['originalError']]
      .map((part) => collectErrorText(part))
      .filter(Boolean)
      .join(' ');
  }

  return String(error);
}

function normalizeRoutePath(url: string): string {
  const withoutQuery = url.split('?')[0].split('#')[0].trim();
  return withoutQuery.replace(/^\/+/, '').replace(/\/+$/, '');
}
